import { Palette } from '@mui/material';

// Enums via the `enums` sub-barrel, not top `#shared` (it re-exports theme/), to keep shared/ acyclic.
import {
  EAircraftStatus,
  EFlightCategory,
  EMaoMaintenanceBadge,
  ENOPOrganType,
  ESeverity,
} from '../../enums';
import { getOpacityHex } from '../helpers';

const defaultMixinOptions = {
  prefix: '',
  primaryOpacity: 1,
  secondaryOpacity: 0.15,
};

export const commonMixins = {
  severityStyles: (palette: Palette, options = {}) => {
    const { prefix, primaryOpacity, secondaryOpacity } = {
      ...defaultMixinOptions,
      ...options,
    };
    return {
      [`${prefix}.severity-status-${ESeverity.CRITICAL}`]: {
        ['--severityStatusColor']: `${palette.severity.critical}${getOpacityHex(primaryOpacity)}`,
        ['--severityStatusSecondaryColor']: `${palette.severity.critical}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--severityStatusColor)`,
        ['--secondaryColor']: `var(--severityStatusSecondaryColor)`,
      },
      [`${prefix}.severity-status-${ESeverity.MAJOR}`]: {
        ['--severityStatusColor']: `${palette.severity.major}${getOpacityHex(primaryOpacity)}`,
        ['--severityStatusSecondaryColor']: `${palette.severity.major}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--severityStatusColor)`,
        ['--secondaryColor']: `var(--severityStatusSecondaryColor)`,
      },
      [`${prefix}.severity-status-${ESeverity.MINOR}`]: {
        ['--severityStatusColor']: `${palette.severity.minor}${getOpacityHex(primaryOpacity)}`,
        ['--severityStatusSecondaryColor']: `${palette.severity.minor}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--severityStatusColor)`,
        ['--secondaryColor']: `var(--severityStatusSecondaryColor)`,
      },
      [`${prefix}.severity-status-${ESeverity.LOW}`]: {
        ['--severityStatusColor']: `${palette.severity.low}${getOpacityHex(primaryOpacity)}`,
        ['--severityStatusSecondaryColor']: `${palette.severity.low}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--severityStatusColor)`,
        ['--secondaryColor']: `var(--severityStatusSecondaryColor)`,
        ['--cardBorderColor']: `${palette.severity.low}`,
      },
      [`${prefix}.severity-status-${ESeverity.NA}`]: {
        ['--severityStatusColor']: `${palette.severity.critical}${getOpacityHex(primaryOpacity)}`,
        ['--severityStatusSecondaryColor']: `${palette.severity.critical}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--severityStatusColor)`,
        ['--secondaryColor']: `var(--severityStatusSecondaryColor)`,
      },
    };
  },
  aircraftStatusStyles: (palette: Palette, options = {}) => {
    const { prefix, primaryOpacity, secondaryOpacity } = {
      ...defaultMixinOptions,
      ...options,
    };
    return {
      [`${prefix}.aircraft-status-${EAircraftStatus.NotAvailable}`]: {
        ['--aircraftStatusColor']: `${palette.aircraftStatus.notAvailable}${getOpacityHex(primaryOpacity)}`,
        ['--aircraftStatusSecondaryColor']: `${palette.aircraftStatus.notAvailable}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--aircraftStatusColor)`,
        ['--secondaryColor']: `var(--aircraftStatusSecondaryColor)`,
      },
      [`${prefix}.aircraft-status-${EAircraftStatus.Available}`]: {
        ['--aircraftStatusColor']: `${palette.aircraftStatus.available}${getOpacityHex(primaryOpacity)}`,
        ['--aircraftStatusSecondaryColor']: `${palette.aircraftStatus.available}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--aircraftStatusColor)`,
        ['--secondaryColor']: `var(--aircraftStatusSecondaryColor)`,
      },
      [`${prefix}.aircraft-status-${EAircraftStatus.Maintenance}`]: {
        ['--aircraftStatusColor']: `${palette.aircraftStatus.maintenance}${getOpacityHex(primaryOpacity)}`,
        ['--aircraftStatusSecondaryColor']: `${palette.aircraftStatus.maintenance}${getOpacityHex(secondaryOpacity)}`,
        ['--aircraftStatusMaintenanceColor']: `${palette.aircraftStatus.maintenance}${getOpacityHex(primaryOpacity)}`,
        ['--aircraftStatusMaintenanceSecondaryColor']: `${palette.aircraftStatus.maintenance}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--aircraftStatusColor)`,
        ['--secondaryColor']: `var(--aircraftStatusSecondaryColor)`,
        ['--tailColor']: `${palette.aircraftStatus.maintenance}`,
        ['--statusLabelColor']: `${palette.aircraftStatus.maintenance}`,
        [`&.maintenance-type-${EMaoMaintenanceBadge.AOG}`]: {
          ['--aircraftStatusColor']: `${palette.aircraftStatus.aog}${getOpacityHex(primaryOpacity)}`,
          ['--aircraftStatusSecondaryColor']: `${palette.aircraftStatus.aog}${getOpacityHex(secondaryOpacity)}`,
          ['--aircraftStatusMaintenanceColor']: `${palette.aircraftStatus.aog}${getOpacityHex(primaryOpacity)}`,
          ['--aircraftStatusMaintenanceSecondaryColor']: `${palette.aircraftStatus.aog}${getOpacityHex(secondaryOpacity)}`,
          ['--primaryColor']: `var(--aircraftStatusColor)`,
          ['--secondaryColor']: `var(--aircraftStatusSecondaryColor)`,
        },
      },
    };
  },
  organStyles: (palette: Palette, options = {}) => {
    const { prefix, primaryOpacity, secondaryOpacity } = {
      ...defaultMixinOptions,
      ...options,
    };
    return {
      [`${prefix}.organ-type-${ENOPOrganType.Heart}`]: {
        ['--organColor']: `${palette.organ.heart}${getOpacityHex(primaryOpacity)}`,
        ['--organSecondaryColor']: `${palette.organ.heart}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--organColor)`,
        ['--secondaryColor']: `var(--organSecondaryColor)`,
      },
      [`${prefix}.organ-type-${ENOPOrganType.Liver}`]: {
        ['--organColor']: `${palette.organ.liver}${getOpacityHex(primaryOpacity)}`,
        ['--organSecondaryColor']: `${palette.organ.liver}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--organColor)`,
        ['--secondaryColor']: `var(--organSecondaryColor)`,
      },
      [`${prefix}.organ-type-${ENOPOrganType.Lungs}`]: {
        ['--organColor']: `${palette.organ.lungs}${getOpacityHex(primaryOpacity)}`,
        ['--organSecondaryColor']: `${palette.organ.lungs}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--organColor)`,
        ['--secondaryColor']: `var(--organSecondaryColor)`,
      },
    };
  },
  flightCategoryStyles: (palette: Palette, options = {}) => {
    const { prefix, primaryOpacity, secondaryOpacity } = {
      ...defaultMixinOptions,
      ...options,
    };
    return {
      [`${prefix}.flight-category-${EFlightCategory.LIFR}`]: {
        ['--flightCategoryColor']: `${palette.flightCategory.LIFR}${getOpacityHex(primaryOpacity)}`,
        ['--flightCategorySecondaryColor']: `${palette.flightCategory.LIFR}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--flightCategoryColor)`,
        ['--secondaryColor']: `var(--flightCategorySecondaryColor)`,
      },
      [`${prefix}.flight-category-${EFlightCategory.IFR}`]: {
        ['--flightCategoryColor']: `${palette.flightCategory.IFR}${getOpacityHex(primaryOpacity)}`,
        ['--flightCategorySecondaryColor']: `${palette.flightCategory.IFR}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--flightCategoryColor)`,
        ['--secondaryColor']: `var(--flightCategorySecondaryColor)`,
      },
      [`${prefix}.flight-category-${EFlightCategory.MVFR}`]: {
        ['--flightCategoryColor']: `${palette.flightCategory.MVFR}${getOpacityHex(primaryOpacity)}`,
        ['--flightCategorySecondaryColor']: `${palette.flightCategory.MVFR}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--flightCategoryColor)`,
        ['--secondaryColor']: `var(--flightCategorySecondaryColor)`,
      },
      [`${prefix}.flight-category-${EFlightCategory.VFR}`]: {
        ['--flightCategoryColor']: `${palette.flightCategory.VFR}${getOpacityHex(primaryOpacity)}`,
        ['--flightCategorySecondaryColor']: `${palette.flightCategory.VFR}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--flightCategoryColor)`,
        ['--secondaryColor']: `var(--flightCategorySecondaryColor)`,
      },
      [`${prefix}.flight-category-NA`]: {
        ['--flightCategoryColor']: `${palette.flightCategory.NA}${getOpacityHex(primaryOpacity)}`,
        ['--flightCategorySecondaryColor']: `${palette.flightCategory.NA}${getOpacityHex(secondaryOpacity)}`,
        ['--primaryColor']: `var(--flightCategoryColor)`,
        ['--secondaryColor']: `var(--flightCategorySecondaryColor)`,
      },
    };
  },
};
